import { useCallback, useEffect, useState } from 'react'
import { Network } from '@capacitor/network'
import { isNativePlatform } from '@/lib/platform/platformDetection'

export function useNativeOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine)
  const isNative = isNativePlatform()

  const handleOnline = useCallback(() => setIsOnline(true), [])
  const handleOffline = useCallback(() => setIsOnline(false), [])

  useEffect(() => {
    if (!isNative) {
      setIsOnline(navigator.onLine)

      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)

      return () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }

    let mounted = true
    let networkHandle: { remove: () => void } | null = null

    Network.getStatus()
      .then(status => {
        if (mounted) setIsOnline(status.connected)
      })
      .catch(() => {
        if (mounted) setIsOnline(navigator.onLine)
      })

    Network.addListener('networkStatusChange', status => {
      if (mounted) setIsOnline(status.connected)
    })
      .then(handle => {
        if (mounted) {
          networkHandle = handle
        } else {
          handle.remove()
        }
      })
      .catch(() => {
        // Fallback to browser events if Capacitor listener fails
      })

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      mounted = false
      networkHandle?.remove()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [isNative, handleOnline, handleOffline])

  return isOnline
}
